import { ArrayUtils, engineComponent, StringUtils, Utils } from "neo-common";
import { BaseElement } from "./BaseElement";
import { ComponentTypes } from "../../ComponentTypes";
import { CustomUtils } from "../../../Utils/CustomUtils";
import { ImageElementAttributes } from "./ImageElement";

/**
 * SpineElement class is class which creates holder for spine object.
 * Examples (Create):
 *  <Item Class="SpineElement" Spine="name"/>
 *  <Item Class="SpineElement" Spine="name" Speed="0.5"/>
 *  <Item Class="SpineElement" Spine="name" Speed="0.5" Animations="Idle"/>
 *  <Item Class="SpineElement" Spine="name" Animations="Idle;true"/>
 *  <Item Class="SpineElement" Spine="name" Speed="0.5" Animations="Idle|Win;false;60|Idle;true;60"/>
 *  <Item Class="SpineElement" Spine="name" Mixes="Idle;Win;30|Win;Idle;30" Animations="Idle|Win;false;60|Idle;true;60|Cycle;true;0;1"/>
 * Examples (Update):
 *  <Item Speed="0.5"/>
 *  <Item Speed="0.5" Animations="Idle"/>
 *  <Item Animations="Idle;true"/>
 *  <Item Speed="0.5" Animations="Idle|Win;false;60|Idle;true"/>
 *  <Item Animations="Empty"/> Stops animations
 */
@engineComponent(ComponentTypes.DisplayObject)
export class SpineElement extends BaseElement {
    static DynamicTint: string = "000000"; // Todo Transform this into array, e.g. DynamicTint['dynamic_symbols'] = '000000' ? And use it as attribute with this style: Tint="dynamic_symbols", Tint="dynamic2",..

    //******************************************************************************************************************
    //  GLOBAL CONSTANTS (PRE-LOADER)
    //******************************************************************************************************************

    public static Data: any;

    public static Atlas: any;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected spine: PIXI.spine.Spine;

    protected mixes: {
        from: string;
        to: string;
        duration: number;
    }[];

    protected skin: string;

    //******************************************************************************************************************
    //  GETTERS, SETTERS
    //******************************************************************************************************************

    public set speed(value: number) {
        this.spine.state.timeScale = value;
    }

    public get speed(): number {
        return this.spine.state.timeScale;
    }

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.spine = null;
        this.mixes = [];

        PIXI.ticker.shared.add(this.update, this, PIXI.UPDATE_PRIORITY.HIGH);
    }

    public update(deltaTime: number): void {
        if (this.visible && this.spine) {
            this.spine.update((deltaTime / PIXI.ticker.shared.speed / PIXI.settings.TARGET_FPMS) * 0.001);
        }
    }

    //******************************************************************************************************************
    //  CREATE, UPDATE
    //******************************************************************************************************************

    public createByNode(node: Element): void {
        super.createByNode(node);

        const spine = node.hasAttribute(SpineElementAttributes.SPINE)
            ? node.getAttribute(SpineElementAttributes.SPINE)
            : "spine";
        const mixes = node.hasAttribute(SpineElementAttributes.MIXES)
            ? ArrayUtils.stringArray(node.getAttribute(SpineElementAttributes.MIXES), "|")
            : [];

        for (let i = 0; i < mixes.length; i++) {
            const mix = ArrayUtils.stringArray(mixes[i]);

            this.mixes.push({ from: mix[0].trim(), to: mix[1].trim(), duration: parseFloat(mix[2]) });
        }

        this.createSpine(spine);
        this.setMixes();
    }

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(SpineElementAttributes.SPEED)) {
            this.spine.state.timeScale = parseFloat(node.getAttribute(SpineElementAttributes.SPEED));
        } else if (node.hasAttribute(SpineElementAttributes.RANDOM_SPEED)) {
            this.spine.state.timeScale = SpineElement.getRandomSpeed(node);
        }

        if (node.hasAttribute(SpineElementAttributes.TINT)) {
            const tint = node.getAttribute(SpineElementAttributes.TINT);
            switch (tint) {
                case "none":
                    this.spine.tint = 0xffffff;
                    break;
                case "dynamic":
                    this.spine.tint = parseInt(SpineElement.DynamicTint, 16);
                    break;
                default:
                    this.spine.tint = parseInt(node.getAttribute(SpineElementAttributes.TINT), 16);
                    break;
            }
        }

        if (node.hasAttribute(SpineElementAttributes.SKIN)) {
            const skin = node.getAttribute(SpineElementAttributes.SKIN);
            if (skin != null) {
                this.setSkin(skin);
            }
        }

        if (node.hasAttribute(SpineElementAttributes.ANIMATIONS)) {
            const animations = ArrayUtils.stringArray(node.getAttribute(SpineElementAttributes.ANIMATIONS), "|");
            const tracks: { name: string; loop?: boolean; delay?: number }[] = [];

            for (let i = 0; i < animations.length; i++) {
                let animation = ArrayUtils.stringArray(animations[i]);
                const track: { name: string; loop?: boolean; delay?: number; track?: number } = {
                    name: animation[0].trim(),
                };

                if (animation.length > 1) {
                    track.loop = StringUtils.parseBoolean(animation[1]);
                }

                if (animation.length > 2) {
                    track.delay = parseInt(animation[2]);
                }

                if (animation.length > 3) {
                    track.track = parseInt(animation[3]);
                }

                tracks.push(track);
            }

            this.animate(tracks);
        }
    }

    //******************************************************************************************************************
    //  SPINE
    //******************************************************************************************************************

    protected createSpine(key: string): void {
        const textureAtlas = new PIXI.spine.core.TextureAtlas(SpineElement.Atlas.data, function (
            line: any,
            callback: any,
        ) {
            line = line.replace(/\.png/g, "");
            callback(PIXI.BaseTexture.from(line));
        });
        const atlasAttachmentLoader = new PIXI.spine.core.AtlasAttachmentLoader(textureAtlas);
        const skeletonJson = new PIXI.spine.core.SkeletonJson(atlasAttachmentLoader);
        const skeletonData = skeletonJson.readSkeletonData(CustomUtils.updateSpineData(SpineElement.Data.data[key]));

        this.spine = new PIXI.spine.Spine(skeletonData);
        this.spine.autoUpdate = false;

        this.addChild(this.spine);
    }

    protected setMixes(): void {
        for (let i = 0; i < this.mixes.length; i++) {
            this.spine.stateData.setMix(this.mixes[i].from, this.mixes[i].to, this.mixes[i].duration / 60);
        }
    }

    public animate(animations: { name: string; loop?: boolean; delay?: number; track?: number }[]): void {
        const tracks: { name: string; loop?: boolean; delay?: number; track?: number }[][] = [];

        animations.forEach((animation): void => {
            const track = Utils.isNullOrUndefined(animation.track) ? 0 : animation.track;

            while (tracks.length < track + 1) {
                tracks.push([]);
            }

            tracks[track].push(animation);
        });

        tracks.forEach((track, i): void => {
            track.forEach((animation, j): void => {
                const name = animation.name.trim();
                const loop = Utils.isNullOrUndefined(animation.loop) ? false : animation.loop;
                const delay = Utils.isNullOrUndefined(animation.delay) ? 0 : animation.delay / 60;

                if (j === 0) {
                    if (name === "Empty") {
                        this.spine.state.setEmptyAnimation(i, 0);
                    } else {
                        this.spine.state.setAnimation(i, name, loop);
                    }
                } else {
                    if (name === "Empty") {
                        this.spine.state.addEmptyAnimation(i, 0, delay);
                    } else {
                        this.spine.state.addAnimation(i, name, loop, delay);
                    }
                }
            });
        });
    }

    public setSkin(skinName: string): void {
        if (skinName != null) {
            this.skin = skinName;
            if (this.spine.skeleton.data.findSkin(skinName)) {
                //this.spine.skeleton.data.defaultSkin
                this.spine.skeleton.setSkinByName(skinName);
            }
        }
    }

    /**
     * Returns random speed from range defined in RandomSpeed="MIN;MAX"
     * @param {Element} node
     * @returns {number}
     */
    private static getRandomSpeed(node: Element): number {
        let result = 1;

        let dataArray = ArrayUtils.numberArray(node.getAttribute(SpineElementAttributes.RANDOM_SPEED));
        if (dataArray.length === 2) {
            let min = dataArray[0];
            let max = min > dataArray[1] ? min : dataArray[1];

            result = SpineElement.getRandom(min, max);
        }

        return result;
    }

    /**
     * Returns random number from range [min;max]
     * @param {number} min
     * @param {number} max
     * @returns {number}
     */
    private static getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    public clone(): SpineElement {
        const element = new SpineElement();
        element.alpha = this.alpha;
        element.name = this.name;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.rotation = this.rotation;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;

        if (this.maskSprite != null) {
            let maskElement = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            maskElement.position.x = this.maskSprite.position.x;
            maskElement.position.y = this.maskSprite.position.y;

            element.maskSprite = maskElement;
            element.addChild(maskElement);
            element.mask = maskElement;
        }

        element.spine = new PIXI.spine.Spine(this.spine.spineData);
        element.spine.autoUpdate = false;
        element.spine.tint = this.spine.tint;

        if (this.skin != null) {
            element.spine.skeleton.setSkinByName(this.skin);
        }

        element.addChild(element.spine);

        element.mixes = this.mixes;
        element.setMixes();

        element.markAsCloned();

        return element;
    }
}

export const SpineElementAttributes = {
    ANIMATIONS: "Animations",
    MIXES: "Mixes",
    RANDOM_SPEED: "RandomSpeed",
    SKIN: "Skin",
    SPEED: "Speed",
    SPINE: "Spine",
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/SpineElement.ts
 **/